import React from 'react';
import { 
  Check, 
  Trophy, 
  Heart, 
  Edit, 
  MapPin, 
  DollarSign, 
  Users,
  TrendingUp,
  TrendingDown,
  GraduationCap,
  ArrowUpDown
} from 'lucide-react';

const CollegeListView = ({ 
  colleges, 
  selectedColleges, 
  onSelectCollege, 
  onEditCollege, 
  sortBy = 'name',
  sortDirection = 'asc',
  onSortChange,
  isParent 
}) => {
  
  // Sort colleges based on sortBy prop
  const sortedColleges = React.useMemo(() => {
    if (!colleges || colleges.length === 0) return [];
    
    const sorted = [...colleges].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'ranking':
          const aRank = a.usnwr_ranking || a.liberal_arts_ranking || 999;
          const bRank = b.usnwr_ranking || b.liberal_arts_ranking || 999;
          comparison = aRank - bRank;
          break;
        case 'acceptance_rate':
          const aRate = a.acceptance_rate || 100;
          const bRate = b.acceptance_rate || 100;
          comparison = aRate - bRate;
          break;
        case 'tuition':
          const aTuition = a.tuition_out_of_state || a.tuition_in_state || 0;
          const bTuition = b.tuition_out_of_state || b.tuition_in_state || 0;
          comparison = aTuition - bTuition;
          break;
        case 'enrollment':
          const aEnroll = a.student_enrollment || 0;
          const bEnroll = b.student_enrollment || 0;
          comparison = bEnroll - aEnroll; // Largest first by default
          break;
        default:
          return 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [colleges, sortBy, sortDirection]);
  
  const getRankingDisplay = (college) => {
    const rankings = [];
    
    if (college.usnwr_ranking) {
      rankings.push({
        type: college.ranking_type === 'liberal_arts_college' ? 'LAC' : 'Natl',
        value: college.usnwr_ranking,
        color: '#3b82f6'
      });
    }
    
    if (college.engineering_ranking) {
      rankings.push({
        type: 'Eng',
        value: college.engineering_ranking,
        color: '#10b981'
      });
    }
    
    if (college.nursing_ranking) {
      rankings.push({
        type: 'Nurs',
        value: college.nursing_ranking,
        color: '#ec4899'
      });
    }
    
    return rankings;
  };

  const formatTuition = (tuition) => {
    if (!tuition) return 'N/A';
    return `$${Math.round(tuition / 1000)}k`;
  };

  const formatEnrollment = (enrollment) => {
    if (!enrollment) return 'N/A';
    if (enrollment > 10000) return `${Math.round(enrollment / 1000)}k`;
    return enrollment.toLocaleString();
  };

  const getAcceptanceRateColor = (rate) => {
    if (!rate) return '#6b7280';
    if (rate < 10) return '#dc2626';
    if (rate < 25) return '#f59e0b';
    if (rate < 50) return '#10b981';
    return '#3b82f6';
  };

  const getSortIcon = (columnKey) => {
    if (sortBy !== columnKey) {
      return <ArrowUpDown size={12} style={{ opacity: 0.4 }} />;
    }
    return sortDirection === 'asc' ? 
      <TrendingUp size={12} /> : 
      <TrendingDown size={12} />;
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Sorting Instructions */}
      <div style={{
        padding: '8px 16px',
        background: '#eff6ff',
        borderBottom: '1px solid #dbeafe',
        fontSize: '12px',
        color: '#1d4ed8',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <ArrowUpDown size={14} />
        <span>Click column headers to sort • Click again to reverse order</span>
      </div>

      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isParent ? 
          '40px 40px 300px 120px 150px 100px 100px 100px 100px 60px' :
          '40px 300px 120px 150px 100px 100px 100px 100px 60px',
        padding: '12px 16px',
        background: '#f9fafb',
        borderBottom: '2px solid #e5e7eb',
        fontSize: '12px',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        <div></div>
        {isParent && <div></div>}
        <div 
          onClick={() => onSortChange?.('name')}
          style={{ 
            cursor: onSortChange ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: sortBy === 'name' ? '#3b82f6' : '#6b7280',
            fontWeight: sortBy === 'name' ? 700 : 600,
            padding: '4px 0',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (onSortChange) e.currentTarget.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            if (onSortChange && sortBy !== 'name') e.currentTarget.style.color = '#6b7280';
          }}
        >
          College {onSortChange && getSortIcon('name')}
        </div>
        <div>Location</div>
        <div 
          onClick={() => onSortChange?.('ranking')}
          style={{ 
            cursor: onSortChange ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: sortBy === 'ranking' ? '#3b82f6' : '#6b7280',
            fontWeight: sortBy === 'ranking' ? 700 : 600,
            padding: '4px 0',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (onSortChange) e.currentTarget.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            if (onSortChange && sortBy !== 'ranking') e.currentTarget.style.color = '#6b7280';
          }}
        >
          Rankings {onSortChange && getSortIcon('ranking')}
        </div>
        <div 
          onClick={() => onSortChange?.('acceptance_rate')}
          style={{ 
            cursor: onSortChange ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: sortBy === 'acceptance_rate' ? '#3b82f6' : '#6b7280',
            fontWeight: sortBy === 'acceptance_rate' ? 700 : 600,
            padding: '4px 0',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (onSortChange) e.currentTarget.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            if (onSortChange && sortBy !== 'acceptance_rate') e.currentTarget.style.color = '#6b7280';
          }}
        >
          Accept % {onSortChange && getSortIcon('acceptance_rate')}
        </div>
        <div 
          onClick={() => onSortChange?.('tuition')}
          style={{ 
            cursor: onSortChange ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: sortBy === 'tuition' ? '#3b82f6' : '#6b7280',
            fontWeight: sortBy === 'tuition' ? 700 : 600,
            padding: '4px 0',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (onSortChange) e.currentTarget.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            if (onSortChange && sortBy !== 'tuition') e.currentTarget.style.color = '#6b7280';
          }}
        >
          Tuition {onSortChange && getSortIcon('tuition')}
        </div>
        <div 
          onClick={() => onSortChange?.('enrollment')}
          style={{ 
            cursor: onSortChange ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: sortBy === 'enrollment' ? '#3b82f6' : '#6b7280',
            fontWeight: sortBy === 'enrollment' ? 700 : 600,
            padding: '4px 0',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (onSortChange) e.currentTarget.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            if (onSortChange && sortBy !== 'enrollment') e.currentTarget.style.color = '#6b7280';
          }}
        >
          Size {onSortChange && getSortIcon('enrollment')}
        </div>
        <div>Type</div>
        <div></div>
      </div>

      {/* List Items */}
      <div style={{ maxHeight: '600px', overflow: 'auto' }}>
        {sortedColleges.map((college, index) => {
          const isSelected = selectedColleges?.includes(college.id);
          const rankings = getRankingDisplay(college);
          
          return (
            <div
              key={college.id}
              style={{
                display: 'grid',
                gridTemplateColumns: isParent ? 
                  '40px 40px 300px 120px 150px 100px 100px 100px 100px 60px' :
                  '40px 300px 120px 150px 100px 100px 100px 100px 60px',
                padding: '12px 16px',
                borderBottom: '1px solid #e5e7eb',
                alignItems: 'center',
                background: isSelected ? '#eff6ff' : (index % 2 === 0 ? 'white' : '#fafafa'),
                transition: 'background 0.2s',
                cursor: 'pointer'
              }}
              onClick={() => onSelectCollege?.(college.id)}
            >
              {/* Checkbox */}
              <div onClick={(e) => e.stopPropagation()}>
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    border: `2px solid ${isSelected ? '#3b82f6' : '#d1d5db'}`,
                    borderRadius: '4px',
                    background: isSelected ? '#3b82f6' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => onSelectCollege?.(college.id)}
                >
                  {isSelected && <Check size={14} color="white" />}
                </div>
              </div>

              {/* Favorite (Parents only) */}
              {isParent && (
                <div>
                  <Heart 
                    size={18} 
                    style={{ 
                      color: college.is_favorite ? '#ef4444' : '#d1d5db',
                      fill: college.is_favorite ? '#ef4444' : 'none',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              )}

              {/* College Name */}
              <div style={{ fontWeight: 500 }}>
                {college.name}
                {college.category && (
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 6px',
                    background: college.category === 'reach' ? '#fce7f3' :
                              college.category === 'target' ? '#dbeafe' :
                              college.category === 'safety' ? '#dcfce7' : '#f3f4f6',
                    color: college.category === 'reach' ? '#be185d' :
                          college.category === 'target' ? '#1e40af' :
                          college.category === 'safety' ? '#15803d' : '#6b7280',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}>
                    {college.category}
                  </span>
                )}
              </div>

              {/* Location */}
              <div style={{ 
                fontSize: '14px', 
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <MapPin size={14} />
                {college.city ? `${college.city}, ${college.state}` : college.state || 'N/A'}
              </div>

              {/* Rankings */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {rankings.map((rank, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '2px 6px',
                      background: rank.color + '20',
                      color: rank.color,
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}
                  >
                    {rank.type} #{rank.value}
                  </div>
                ))}
                {rankings.length === 0 && (
                  <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>
                )}
              </div>

              {/* Acceptance Rate */}
              <div style={{ 
                fontSize: '14px',
                fontWeight: 500,
                color: getAcceptanceRateColor(college.acceptance_rate)
              }}>
                {college.acceptance_rate ? `${college.acceptance_rate}%` : '—'}
              </div>

              {/* Tuition */}
              <div style={{ 
                fontSize: '14px',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <DollarSign size={14} />
                {formatTuition(college.tuition_out_of_state || college.tuition)}
              </div>

              {/* Enrollment */}
              <div style={{ 
                fontSize: '14px',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Users size={14} />
                {formatEnrollment(college.student_enrollment)}
              </div>

              {/* Type */}
              <div style={{ fontSize: '13px' }}>
                <span style={{
                  padding: '2px 8px',
                  background: college.type === 'public' ? '#e0f2fe' : '#fef3c7',
                  color: college.type === 'public' ? '#0369a1' : '#92400e',
                  borderRadius: '4px',
                  fontWeight: 500
                }}>
                  {college.type || college.control || 'Unknown'}
                </span>
              </div>

              {/* Edit Button */}
              <div 
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCollege?.(college);
                }}
                style={{
                  padding: '6px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Edit size={16} color="#6b7280" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CollegeListView;